import { useEffect, useRef } from 'react';
import type { MatchPublicView } from '../api-client.js';
import {
  getPermissionState,
  isTurnNotificationsEnabled,
  notificationsSupported,
} from './notification-prefs.js';

/**
 * Bot users are recognised by their stable id prefix (`bot_`).
 * Inlined here so this PR doesn't depend on the bot-display
 * module from #38 — same convention, one line.
 */
function isBotUserId(userId: string | null | undefined): boolean {
  return !!userId && userId.startsWith('bot_');
}

/**
 * Fires a browser notification when it becomes the viewer's turn
 * and the page is hidden (or the window is unfocused). No-op if:
 *   - The browser doesn't support Notifications, or
 *   - The user hasn't opted in (via NotificationToggle), or
 *   - Permission isn't granted, or
 *   - The page is visible right now (the user already sees the
 *     state change), or
 *   - The opponent is a bot (they take turns within a few hundred
 *     ms — notification spam).
 *
 * Dedup: tracks the last decisionWindow we notified about, so
 * the same window doesn't fire twice (e.g. SSE reconnect →
 * refetch → identical state).
 */
export function useTurnNotifications(input: {
  match: MatchPublicView | null;
  userSeatId: string | null;
  enabled: boolean;
}): void {
  const lastNotifiedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const { match, userSeatId, enabled } = input;
    if (!enabled || !match || !userSeatId) return;
    if (!notificationsSupported()) return;
    if (!isTurnNotificationsEnabled()) return;
    if (getPermissionState() !== 'granted') return;
    if (match.match.status !== 'in_progress') return;

    const decisionWindow = match.decisionWindow;
    if (!decisionWindow || decisionWindow.activeSeatId !== userSeatId) return;

    // Skip if any opponent is a bot — bots take turns instantly,
    // notifications would be a stream of useless pings.
    const hasBotOpponent = match.participants.some(
      (p) => p.seatId !== userSeatId && isBotUserId(p.userId),
    );
    if (hasBotOpponent) return;

    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      return;
    }

    // Dedup key bundles match-id + the action count at the moment
    // the window opened. The action count moves on every play, so
    // a fresh "your turn" window will have a different key.
    const key = `${match.match.id}::${match.actionCount}`;
    if (lastNotifiedKeyRef.current === key) return;
    lastNotifiedKeyRef.current = key;

    try {
      const n = new Notification(`Your turn — ${match.game.name}`, {
        body: 'Tap to take your turn.',
        tag: `turn:${match.match.id}`,
        icon: '/favicon.ico',
      });
      n.onclick = () => {
        if (typeof window !== 'undefined') window.focus?.();
        n.close();
      };
    } catch {
      // Some browsers throw if Notification is constructed too
      // early — swallow; the next state push will retry.
    }
  }, [input]);
}
